import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { useToast } from '@/components/ui/use-toast';
import type { InitialAuthState } from '@/lib/ssr-state';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: any) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  signUpWithStatus?: (email: string, password: string, userData: any) => Promise<{ isExistingUser: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: React.ReactNode;
  initialAuthState?: InitialAuthState;
};

const getBrowserOrigin = () => (typeof window !== 'undefined' ? window.location.origin : 'https://www.canxp.ai');

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, initialAuthState }) => {
  const [user, setUser] = useState<User | null>(initialAuthState?.session?.user ?? null);
  const [session, setSession] = useState<Session | null>(initialAuthState?.session ?? null);
  const [loading, setLoading] = useState(initialAuthState === undefined);
  const { toast } = useToast();

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_IN') {
        trackEvent('login');
        try {
          const existing = typeof localStorage !== 'undefined' ? localStorage.getItem('trust_mode_enabled') : null;
          if (existing === null) {
            localStorage.setItem('trust_mode_enabled', 'true');
          }
        } catch {
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast({ title: 'Login failed', description: error.message, variant: 'destructive' });
      throw error;
    }
  };

  const signUpWithStatus = async (email: string, password: string, userData: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: userData, emailRedirectTo: `${getBrowserOrigin()}/auth` },
    });

    if (error) {
      throw error;
    }

    const identitiesCount = data.user?.identities?.length ?? 0;
    return { isExistingUser: identitiesCount === 0 };
  };

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      const { isExistingUser } = await signUpWithStatus(email, password, userData);
      if (isExistingUser) {
        toast({
          title: 'Email already registered',
          description:
            "This email is already associated with an account. We've sent you a sign-in/verification email. If you forgot your password, use 'Forgot password'.",
        });
      } else {
        toast({ title: 'Account created', description: 'Check your email to verify your account.' });
      }
    } catch (error: any) {
      toast({ title: 'Signup failed', description: error.message, variant: 'destructive' });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await trackEvent('logout');
    } catch (analyticsError) {
      console.warn('Failed to track logout event:', analyticsError);
    }

    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      toast({ title: 'Sign out failed', description: error.message, variant: 'destructive' });
      throw error;
    }
  };

  const refreshSession = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('Error refreshing session:', error);
      return;
    }

    setSession(data.session);
    setUser(data.user);
  };

  const resendVerificationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${getBrowserOrigin()}/auth` },
    });

    if (error) {
      toast({ title: 'Resend failed', description: error.message, variant: 'destructive' });
      throw error;
    }

    toast({ title: 'Verification email sent', description: 'Check your inbox for the link.' });
  };

  const value = useMemo(
    () => ({ user, session, loading, signIn, signUp, signOut, refreshSession, resendVerificationEmail, signUpWithStatus }),
    [loading, session, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
